import { Component, computed, effect, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ServicePortalApi } from '../../../api/api';
import { CustomerAccount, CustomerAccountInput, UserCredentials } from '../../../api/types';
import {
  SynBreadcrumbComponent,
  SynBreadcrumbItemComponent,
  SynButtonComponent,
  SynCheckboxComponent,
  SynDialogComponent,
  SynIconComponent,
} from '@synergy-design-system/angular';
import { CustomerAccountFormComponent } from './customer-account-form.component';
import { PasswordInputComponent } from '../../components/password-input.component';
import { getUserData } from '../../../api/auth';
import { handleError, setDuplicateUsernameError } from '../../../errors';
import { SavedToastComponent } from '../../components/saved-toast.component';

@Component({
  selector: 'app-edit-customer-account',
  template: `
    <syn-breadcrumb>
      <syn-breadcrumb-item routerLink="" i18n="@@home">Home</syn-breadcrumb-item>
      <syn-breadcrumb-item routerLink="/customers" i18n="@@customer-accounts">Customer Accounts</syn-breadcrumb-item>
      <syn-breadcrumb-item>{{ account()?.username ?? '' }}</syn-breadcrumb-item>
    </syn-breadcrumb>

    <div class="flex-row gap-2 align-center margin-bottom-1">
      <h1 i18n="@@edit-customer-account">Edit Customer Account</h1>
      <syn-button
        (click)="openResetPasswordDialog()"
        size="small"
        data-e2e="btn-reset-password"
      >
        <syn-icon name="lock_fill" slot="prefix" />
        <span i18n="@@reset-password">Reset Password</span>
      </syn-button>

      @if (hasOtpSetup()) {
        <syn-button
          (click)="openResetMfaDialog()"
          size="small"
        >
          <syn-icon name="key_fill" slot="prefix" />
          <span i18n="@@remove-mfa">Remove MFA</span>
        </syn-button>
      }

      @if (isAdmin()) {
        <syn-button
          (click)="openDeleteDialog()"
          size="small"
        >
          <syn-icon name="delete_fill" slot="prefix" />
          <span i18n="@@delete-customer-account">Delete Customer Account</span>
        </syn-button>
      }
    </div>

    <!-- Add loading indication until data is available -->
    @if (account()) {
      <app-customer-account-form
        [account]="account()!"
        [ssus]="ssus()"
        [isAdmin]="isAdmin()"
        [lastLogin]="account()?.lastLogin"
        [lastEmailVerificationTriggered]="account()?.lastEmailVerificationTriggered"
        [creationTimestamp]="account()?.creationTimestamp"
        submitIcon="save"
        submitText="Save Changes"
        i18n-submitText="@@save-changes"
        (submitAccount)="onSubmit($event)"
      />
    }

    <app-saved-toast #savedToast />

    <syn-dialog
      label="Delete Customer Account"
      i18n-label="@@delete-customer-account"
      #deleteDialog>
      <div>
        <p i18n="@@delete-customer-account-warning">
          Do you want to <strong>permanently delete</strong> the following customer account?
        </p>
        <span>{{ this.displayName() }}</span>
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeDeleteDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onDeleteAccount()" [disabled]="requestRunning()">
          <span i18n="@@delete-customer-account">Delete Customer Account</span>
        </syn-button>
      </div>
    </syn-dialog>

    <syn-dialog
      label="Reset Password"
      i18n-label="@@reset-password"
      #resetPasswordDialog>
      <div class="flex-col gap-2">
        <app-password-input
          label="Password" i18n-label="@@password"
          [(password)]="password"
          [alwaysShowRequirements]="true"
          #passwordInput
        />
        <syn-checkbox
          [(checked)]="temporary"
        >
          <span i18n="@@temporary">Temporary</span>
        </syn-checkbox>
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeResetPasswordDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onResetPassword()"
                    [disabled]="requestRunning() || !passwordInput.isValid()">
          <span i18n="@@reset-password">Reset Password</span>
        </syn-button>
      </div>
    </syn-dialog>

    <syn-dialog
      label="Reset MFA"
      i18n-label="@@reset-mfa"
      #resetMfaDialog>
      <div class="flex-col gap-2">
        <syn-checkbox
          [(checked)]="resetOtp"
        >
          <span i18n="@@otp">Time-based authenticators (6 digit codes)</span>
        </syn-checkbox>

        <!-- In the future, add Passkey reset option here -->
      </div>
      <div slot="footer" class="flex-row gap-1 justify-end">
        <syn-button i18n="@@cancel" (click)="closeResetMfaDialog()">Cancel</syn-button>
        <syn-button variant="filled" (click)="onResetMfa()"
                    [disabled]="requestRunning()">
          <span i18n="@@reset-mfa">Reset MFA</span>
        </syn-button>
      </div>
    </syn-dialog>
  `,
  styles: [],
  imports: [
    SynButtonComponent,
    SynIconComponent,
    SynDialogComponent,
    RouterLink,
    SynBreadcrumbComponent,
    SynBreadcrumbItemComponent,
    CustomerAccountFormComponent,
    SynCheckboxComponent,
    PasswordInputComponent,
    SavedToastComponent,
  ],
})
export class EditCustomerAccountComponent {
  userId = inject(ActivatedRoute).snapshot.params['userId'];
  api = inject(ServicePortalApi);
  router = inject(Router);

  account: WritableSignal<CustomerAccount | null> = signal(null);
  displayName = computed(() => {
    const account = this.account();
    if (account == null) return '(unknown)';

    return account.firstName + ' ' + account.lastName + ' (' + account.username + ')';
  });


  password = signal('');
  temporary = signal(true);

  resetOtp = signal(true);

  requestRunning = signal(false);
  isAdmin = signal(false);
  ssus = signal<string[]>([]);

  deleteDialog = viewChild<SynDialogComponent>('deleteDialog');
  resetPasswordDialog = viewChild<SynDialogComponent>('resetPasswordDialog');
  resetMfaDialog = viewChild<SynDialogComponent>('resetMfaDialog');
  savedToast = viewChild.required<SavedToastComponent>('savedToast');

  hasOtpSetup = computed(() => {
    const account = this.account();
    if (account == null) return false;
    return account.hasOtpSetup ?? false;
  });

  constructor() {
    this.api.getCustomer(this.userId, (value) => {
      this.fillDefaultFields(value);
      this.account.set(value);
    });

    this.isAdmin.set(getUserData().roles.includes('sickid_usermanager_admin'));

    // SSUs must be fetched only when the user is an admin
    effect(() => {
      if (this.isAdmin()) {
        this.api.getSsus(({ ssus }) => {
          this.ssus.set(ssus.toSorted());
        });
      }
      const userData = getUserData();
      if (userData.managedSsu && userData.managedSsu.trim().length > 0) {
        const ssus = userData.managedSsu
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0);
        this.ssus.set(ssus.toSorted());
      }
    });
  }

  openDeleteDialog() {
    void this.deleteDialog()?.nativeElement?.show();

  }

  closeDeleteDialog() {
    void this.deleteDialog()?.nativeElement?.hide();
  }

  onDeleteAccount() {
    this.requestRunning.set(true);
    this.api.deleteCustomer(this.userId, () => {
      console.log('Customer account delete', this.userId);
      this.requestRunning.set(false);
      void this.router.navigate(['customers']);
    });
  }

  openResetPasswordDialog() {
    this.password.set('');
    this.temporary.set(true);
    void this.resetPasswordDialog()?.nativeElement?.show();
  }

  closeResetPasswordDialog() {
    void this.resetPasswordDialog()?.nativeElement?.hide();
  }

  onResetPassword() {
    const credentials: UserCredentials = {
      password: this.password(),
      temporary: this.temporary(),
    };

    this.requestRunning.set(true);
    this.api.updateCustomerCredentials(this.userId, credentials, () => {
      console.log('Updated credentials');
      this.closeResetPasswordDialog();
      this.requestRunning.set(false);
    });
  }

  openResetMfaDialog() {
    this.resetOtp.set(true);
    void this.resetMfaDialog()?.nativeElement?.show();
  }

  closeResetMfaDialog() {
    void this.resetMfaDialog()?.nativeElement?.hide();
  }

  onResetMfa() {
    const credentials: UserCredentials = {
      removeOtps: this.resetOtp(),
    };

    this.requestRunning.set(true);
    this.api.updateCustomerCredentials(this.userId, credentials, () => {
      console.log('Updated credentials');
      this.closeResetMfaDialog();
      this.requestRunning.set(false);
    });
  }

  onSubmit(account: CustomerAccountInput) {
    this.api.updateCustomer(this.userId, account, (updatedAccount) => {
        this.fillDefaultFields(updatedAccount);
        this.account.set(updatedAccount);
        this.savedToast().toast();
      },
      (error) => {
        if (error.status === 409) {
          setDuplicateUsernameError();
        } else {
          handleError(error);
        }
      });
  }

  fillDefaultFields(account: CustomerAccountInput) {
    if (!account.salutation) {
      account.salutation = '-';
    }
  }
}
